<template>
  <!-- Bascule Espace candidat <-> recruteur, affichée DIRECTEMENT (pas de menu
       déroulant) au-dessus du footer du menu latéral. Visible seulement si
       l'utilisateur a des menus recruteur (=> Skilloscope). -->
  <div v-if="appMenuStore.hasRecruiterMenus" class="ms-inline">
    <div v-if="loading" class="row justify-center q-py-sm">
      <q-spinner color="white" size="22px" />
    </div>

    <!-- MODE CANDIDAT : un simple bouton pour basculer en espace recruteur.
         Aucun titre ni société affichés par défaut. -->
    <template v-else-if="!recruiterMode">
      <q-btn v-if="companies.length"
             unelevated no-caps align="left" class="ms-enter-btn full-width"
             icon="work"
             :label="tt('sos.recruiter.switchToRecruiter', 'Espace recruteur')"
             @click="enterPrimary" />
      <q-btn v-else
             unelevated no-caps align="left" class="ms-enter-btn full-width"
             icon="add_business"
             :label="tt('sos.recruiter.createCompany', 'Créer ma société')"
             @click="goCreateCompany" />
    </template>

    <!-- MODE RECRUTEUR : bouton « Espace candidat » pour revenir, puis le titre
         « Société active » et la liste des sociétés en dessous. -->
    <template v-else>
      <q-btn unelevated no-caps align="left" class="ms-enter-btn full-width"
             icon="school"
             :label="tt('sos.recruiter.backToCandidate', 'Espace candidat')"
             @click="exit" />

      <div class="ms-title">{{ tt('sos.recruiter.switchCompany', 'Société active') }}</div>

      <q-list dense class="ms-list">
        <q-item
          v-for="c in companies" :key="c.id"
          clickable dense
          :active="c.id === activeCompanyId"
          active-class="ms-active"
          @click="enter(c)"
        >
          <q-item-section avatar>
            <q-avatar size="24px" color="white" text-color="indigo-8" icon="business" />
          </q-item-section>
          <q-item-section>
            <q-item-label class="ms-name">{{ c.name }}</q-item-label>
            <q-item-label caption class="ms-caption">{{ c.role === 'OWNER' ? tt('sos.recruiterCompany.roleOwner', 'Propriétaire') : tt('sos.recruiterCompany.roleMember', 'Membre') }}</q-item-label>
          </q-item-section>
          <q-item-section side v-if="c.id === activeCompanyId">
            <q-icon name="check" color="white" size="18px" />
          </q-item-section>
        </q-item>

        <!-- « Créer ma société » dès que l'utilisateur n'en POSSÈDE aucune. -->
        <q-item v-if="!ownsCompany" clickable dense @click="goCreateCompany">
          <q-item-section avatar><q-icon name="add_business" color="white" size="20px" /></q-item-section>
          <q-item-section>{{ tt('sos.recruiter.createCompany', 'Créer ma société') }}</q-item-section>
        </q-item>
      </q-list>
    </template>
  </div>
</template>

<script>
import { mapStores } from 'pinia';
import { useAppMenuStore } from '@core/stores/app/appMenuStore';
import AxiosService from '@core/services/app/axiosService';
import authHeader from '@core/services/app/authHeader';
import AppMenuService from '@core/services/app/appMenuService';

export default {
  name: 'app-recruiter-mode-switch',

  data() {
    return { companies: [], loading: false };
  },

  computed: {
    ...mapStores(useAppMenuStore),
    recruiterMode() { return this.appMenuStore.recruiterMode; },
    activeCompanyId() { return this.appMenuStore.activeCompanyId; },
    ownsCompany() { return this.companies.some(c => c.role === 'OWNER'); },
  },

  methods: {
    tt(key, fallback) {
      const v = this.$t(key);
      return (v === key || v == null) ? fallback : v;
    },
    async loadCompanies() {
      this.loading = true;
      try {
        const res = await AxiosService.get('/sosRecruiterCompany/myCompanies', { headers: authHeader() });
        this.companies = res.data?.companies || [];
      } catch (e) {
        this.companies = [];
      } finally {
        this.loading = false;
      }
    },
    /** Bascule directe en mode recruteur : société possédée en priorité, sinon la première. */
    enterPrimary() {
      const target = this.companies.find(c => c.role === 'OWNER') || this.companies[0];
      if (target) this.enter(target);
    },
    enter(c) {
      this.appMenuStore.setActiveCompany(c.id, c.name);
      this.appMenuStore.setRecruiterMode(true);
      const target = this.$router.hasRoute('sos-recruiter-dashboard')
        ? 'sos-recruiter-dashboard'
        : (this.$router.hasRoute('sos-recruiter-campaign') ? 'sos-recruiter-campaign' : null);
      if (target) this.$router.push({ name: target }).catch(() => {});
    },
    exit() {
      this.appMenuStore.setRecruiterMode(false);
      this.$router.push('/').catch(() => {});
    },
    async goCreateCompany() {
      // La route SOS est dynamique : générée seulement si le menu SOS_RECRUITER_COMPANY
      // est chargé ET autorisé pour le profil. Ne JAMAIS échouer en silence (bouton muet) :
      // on tente, sinon on recharge le menu (régénère les routes) et on réessaie, sinon on prévient.
      if (await this.pushToCompany()) return;
      await AppMenuService.loadMenu();
      if (await this.pushToCompany()) return;
      this.$q.notify({
        type: 'warning',
        position: 'top',
        message: this.tt('sos.recruiter.companyUnavailable',
          "L'espace société n'est pas accessible depuis ce profil. Contactez l'administrateur."),
      });
    },
    async pushToCompany() {
      if (this.$router.hasRoute('sos-recruiter-company')) {
        await this.$router.push({ name: 'sos-recruiter-company' }).catch(() => {});
        return true;
      }
      return false;
    },
  },

  created() {
    this.loadCompanies();
  },
};
</script>

<style lang="scss" scoped>
// Le composant est TOUJOURS embarqué dans la card utilisateur (footer-bar violette
// du menu) : pas de fond/ombre propre, sinon effet « card dans une card ».
.ms-inline {
  color: #fff;
  padding: 2px 0 0;
}
.ms-title {
  font-size: 0.7rem;
  font-weight: 800;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.8);
  padding: 4px 8px 4px;
}
// Bouton de bascule (mode candidat) : translucide blanc sur le fond violet.
.ms-enter-btn {
  background: rgba(255, 255, 255, 0.16);
  color: #fff;
  border-radius: 10px;
  font-weight: 600;
  &:hover { background: rgba(255, 255, 255, 0.26); }
}
.ms-list { border-radius: 8px; }
.ms-list .q-item {
  border-radius: 8px;
  min-height: 38px;
  color: #fff;
}
.ms-list .q-item:hover { background: rgba(255, 255, 255, 0.14); }
.ms-name { font-weight: 600; color: #fff; }
.ms-caption { color: rgba(255, 255, 255, 0.72) !important; }
.ms-active { background: rgba(255, 255, 255, 0.2); }
</style>
